import { LauncherClient } from "../../clients/launcherClient";
import { ConfigClient } from "../../clients/configClient";
import { Logger } from "../../clients/logger";
import { Globals } from "../../common";
import { SpawnParams } from "../window/types";
import { ResponderMessage, RouterClient } from "../../clients/routerClient";

declare let fin: NonNullable<typeof Globals.fin>;

type Config = Record<string, any>;
type CSFRTokens = Record<string, any>;

function defaultSpawnParams(state: string, spawnParams: SpawnParams): SpawnParams {
	if (!spawnParams) spawnParams = {};
	if (!spawnParams.options)
		spawnParams.options = {
			top: "center",
			left: "center",
		};
	if (!spawnParams.options.customData) spawnParams.options.customData = {};
	spawnParams.options.customData.OAuthState = state;
	spawnParams.options.customData.foreign = {
		services: {
			windowService: {
				allowTabbing: false,
				allowSnapping: false,
				allowGrouping: false,
			},
		},
	};

	spawnParams.canGroup = false;
	return spawnParams;
}

export class Password {
	/**
	 * Begins a PASSWORD authentication. This very simply launches the authentication component which is responsible for
	 * checking credentials and calling authenticationClient.publishAuthorization()
	 * @param {object} config - The authenticate profile associated with this instance. This is set in the config finsemble->authentication. Example is "startup".
	 * @param {object} [spawnParams] - Parameters to pass to <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#spawn">LauncherClient.spawn()</a>.
	 * @param {function} cb
	 */
	authenticate(config: Config, spawnParams: SpawnParams, cb: Function): void {
		Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:PASSWORD:authenticate");
		LauncherClient.spawn(config.component, spawnParams, (err) => {
			Logger.system.debug("APPLICATION LIFECYCLE:STARTUP:AuthenticationService:Authentication spawned");
			if (err) {
				Logger.system.error(`Failed To Spawn Authentication Component: ${JSON.stringify(err)}`);
				cb(err, null);
			} else {
				cb(null, "succcess");
			}
		});
	}
}

export class OAuth {
	tokens: CSFRTokens;

	constructor() {
		this.tokens = {}; // Keeps track of CSRF tokens during authentication phase
		RouterClient.addResponder("authentication.completeOAUTH", (err, queryEvent) => {
			this.responseFromAuthorizationEndpoint(queryEvent);
		});
	}

	/**
	 * Parses a JWT token and returns an object containing those name values
	 * @param {object} token a JWT token
	 * @private
	 */
	private parseJwt(token: string) {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace("-", "+").replace("_", "/");
		return JSON.parse(window.atob(base64));
	}

	/**
	 * Creates a default redirect_uri if one doesn't exist
	 * @private
	 */
	private defaultRedirectURI(config: Config, cb: Function): void {
		ConfigClient.getValue({ field: "finsemble.applicationRoot" }, (err, applicationRoot) => {
			config.redirect_uri = `${applicationRoot}/components/authentication/oauthResponse.html`;
			cb(true);
		});
	}

	/**
	 * Validates that the required config items exist
	 * @private
	 */
	private validate(config: Config, cb: Function): void {
		const requiredFields = {
			authorization_endpoint: true,
			// backchannel_endpoint: true,
			client_id: true,
		};
		let error = "";
		for (const field in requiredFields) {
			if (!config[field]) {
				if (error != "") error = `${error},`;
				error = error + field;
			}
		}
		if (error) {
			error = `OAUTH2 requires the following config items which are missing: ${error}`;
			Logger.system.error(error);
			cb(false);
			return;
		}
		if (!config.grant_type) config.grant_type = "authorization_code";
		if (!config.redirect_uri) {
			this.defaultRedirectURI(config, cb);
			return;
		}
		cb(true);
	}

	/**
	 * Generates a CSRF token
	 * @private
	 */
	private csrf(): string {
		const epoch = new Date();
		let id = epoch.getTime().toString(36);
		id = id + Math.floor(Math.random() * Math.pow(36, 2)).toString(36);
		return id.toUpperCase();
	}

	/**
	 * Begins an OAUTH2 authentication transaction. This requires an authentication profile in finsemble->authentication that contains
	 * the following members: authorization_endpoint, backchannel_endpoint, client_id and redirect_uri
	 * @param {object} config - The authenticate profile associated with this instance. This is set in the config finsemble->authentication. Example is "startup".
	 * @param {object} [spawnParams] - Parameters to pass to <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#spawn">LauncherClient.spawn()</a>.
	 * @param {function} cb
	 */
	authenticate(config: Config, spawnParams: SpawnParams, cb: Function): void {
		this.validate(config, (passed: boolean) => {
			if (!passed) return;
			const response_type = "code";
			const state = this.csrf();
			this.tokens[state] = { config, cb };
			try {
				const url = new URL(config.authorization_endpoint);
				const { searchParams } = url;
				searchParams.set("client_id", config.client_id);

				if (!config.client_secret) {
					searchParams.set("response_type", "token");
				} else {
					searchParams.set("response_type", response_type);
				}

				if (config.nonce) {
					searchParams.set("nonce", this.csrf());
				}

				if (config.scope) {
					searchParams.set("scope", config.scope);
				}

				searchParams.set("redirect_uri", config.redirect_uri);
				searchParams.set("state", state);
				url.search = searchParams.toString();
				// We override the url with the one we've just created, which contains the appropriate query string parameters
				spawnParams = defaultSpawnParams(state, spawnParams);
				spawnParams.url = url.toString();
				// This is to allow the login component to go back to the initial authorization url if needed.
				if (!spawnParams.options) spawnParams.options = { customData: {} };
				spawnParams.options.customData.authorizationUrl = url.toString();

				fin.desktop.System.getSDServerInfo((info) => {
					const sddIsEnabled = info.enabled;

					LauncherClient.spawn(config.component, spawnParams, (err, response) => {
						// When the spawned OAuth login window is closed
						// we need to decide whether to shutdown finsemble
						response?.finWindow.addEventListener("closed", () => {
							// We shutdown finsemble if we're not in SDD mode and
							// we haven't received positive confirmation that the authentication redirect was completed
							if (
								!sddIsEnabled &&
								!Object.values(this.tokens).some(
									(transaction) => (transaction as Record<string, any>).didRedirect === true
								)
							) {
								RouterClient.transmit("Application.shutdown", {});
							}
						});

						Logger.system.info("Authentication spawned");
						if (err) {
							Logger.system.error(`Failed To Spawn Authentication Component: ${JSON.stringify(err)}`);
						}
					});
				});
			} catch (e) {
				Logger.system.error(`Authentication: OAUTH2 failed: ${(e as Error).message}`);
			}
		});
	}

	/**
	 * This function handles the response from the backchannel endpoint. This is the final step before returning back to the client.
	 * @param {object} response The response from the backchannel endpoint
	 * @param {string} state The token state
	 * @param {object} queryEvent The query event to respond on
	 * @private
	 */
	private responseFromBackchannelEndpoint(response: string, state: string, queryEvent: ResponderMessage<any>): void {
		try {
			// Credentials should contain access_token, id_token, expires_in, token_type
			const transaction = this.tokens[state];
			const { config } = transaction;
			var { cb } = transaction;
			const credentials = JSON.parse(response);
			// If we got an id_token back, then parse it and add those items to the credentials
			let moreCredentials: Record<string, any> = {};
			if (credentials.id_token) moreCredentials = this.parseJwt(credentials.id_token);
			for (const field in moreCredentials) {
				credentials[field] = moreCredentials[field];
			}
			// This can be helpful to the receiver
			credentials.config = {
				name: config.name,
			};
			queryEvent.sendQueryResponse(null, credentials);
			if (cb) cb(null, credentials);
		} catch (e: unknown) {
			queryEvent.sendQueryResponse((e as Error).message);
			if (cb) cb((e as Error).message);
		}
		delete this.tokens[state];
	}

	/**
	 * This completes the OAUTH2 process. queryEvent.data.params should contain the query string parameters
	 * received by oauthResponse.html
	 * @param {object} queryEvent
	 * @private
	 */
	private responseFromAuthorizationEndpoint(queryEvent: ResponderMessage<any> | undefined): void {
		const self = this;
		if (!queryEvent) {
			Logger.system.error(`Undefined queryEvent received from authentication.completeOAUTH`);
			return;
		}
		let { err } = queryEvent.data;
		const { params } = queryEvent.data;
		const { state } = params;
		const transaction = this.tokens[state];
		if (!state) {
			err = "No state returned from endpoint";
		}
		if (!err && !transaction) {
			err = `Cannot find OAUTH state ${state}`;
		}

		if (err) {
			if (transaction) {
				const { cb } = transaction;
				cb(err, null);
				delete this.tokens[state];
			}
			queryEvent.sendQueryResponse(err, null);
			return;
		}

		transaction.didRedirect = true;

		const { config } = transaction;

		if (config.backchannel_endpoint) {
			let payload = `code=${params.code}&client_id=${config.client_id}&redirect_uri=${config.redirect_uri}&grant_type=${config.grant_type}`;
			if (config.client_secret) {
				payload = `${payload}&client_secret=${config.client_secret}`;
			}

			const xhr = new XMLHttpRequest();
			xhr.open("POST", config.backchannel_endpoint);
			xhr.onreadystatechange = function () {
				if (xhr.readyState > 3) {
					if (xhr.status == 200) {
						self.responseFromBackchannelEndpoint(xhr.responseText, state, queryEvent);
					} else {
						queryEvent.sendQueryResponse(`OAUTH error: OP response=${xhr.status}:${xhr.responseText}`);
						delete self.tokens[state];
					}
				}
			};
			xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
			xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
			xhr.send(payload);
		} else {
			// we're in 'Implicit Flow with Form Post'
			queryEvent.sendQueryResponse(null, {
				config: {
					name: config.name,
				},
				code: params.code,
			});
		}
	}
}
